import { Button, InputAdornment } from '@mui/material';
import { FC, useCallback } from 'react';

import { StyledTextField } from 'components';
import { ReeferField, ReeferInstructionField } from 'utils';
import { useNewTransaction } from '..';

/*
 * A cleared field is "not specified", never 0: `Number('')` is 0, which recorded 0 °C for a field the
 * inspector emptied. `undefined` means the text is not a number yet, and the value is left alone.
 */
const readingFrom = (text: string, min: number, max: number): number | null | undefined => {
  if (text === '') return null;
  const value = Number(text);
  return isNaN(value) ? undefined : Math.max(min, Math.min(max, value));
};

/* The booking's instruction under a field, with Apply where the field says something else. */
const BookingInstruction: FC<{
  instruction: ReeferInstructionField;
  disabled: boolean;
  onApply: () => void;
  ['data-cy']: string;
}> = ({ instruction, disabled, onApply, ...props }) => (
  <span className="d-flex align-items-center justify-content-between g-2">
    <span data-cy={props['data-cy']}>Booking: {instruction.text}</span>
    {!instruction.canApply ? null : (
      <Button
        size="small"
        disabled={disabled}
        onClick={onApply}
        sx={{ minWidth: 0, py: 0, lineHeight: 1.5 }}
        data-cy={`${props['data-cy']}-apply`}
      >
        APPLY
      </Button>
    )}
  </span>
);

const ReeferInformation: FC = () => {
  const { container, genset, setContainer, setGenset, reeferInstructions, applyBookingReefer, releaseReeferField } =
    useNewTransaction();
  const updateTemperature = useCallback(
    (t: string) => {
      const temperature = readingFrom(t, -100, 100);
      if (temperature === undefined) return;
      releaseReeferField('temperature');
      setContainer(c => ({ ...c, temperature }));
    },
    [releaseReeferField, setContainer]
  );
  const updateVentilation = useCallback(
    (v: string) => {
      releaseReeferField('ventilation');
      setContainer(c => ({ ...c, ventilation: v }));
    },
    [releaseReeferField, setContainer]
  );
  const updateHumidity = useCallback(
    (h: string) => {
      const humidity = readingFrom(h, 0, 100);
      if (humidity === undefined) return;
      releaseReeferField('humidity');
      setContainer(c => ({ ...c, humidity }));
    },
    [releaseReeferField, setContainer]
  );
  const updateFuelLevel = useCallback(
    (f: any) => (isNaN(Number(f)) ? null : setGenset(g => ({ ...g, fuelLevel: Math.max(0, Math.min(4, Number(f))) }))),
    [setGenset]
  );

  const noUnit = !container?.unitNumber?.length;
  const instruction = (field: ReeferField) =>
    !reeferInstructions ? undefined : (
      <BookingInstruction
        instruction={reeferInstructions.fields[field]}
        disabled={noUnit}
        onApply={() => applyBookingReefer(field)}
        data-cy={`reefer-booking-${field}`}
      />
    );

  return (
    <div className="card-primary">
      <div className="card-header px-3 d-flex justify-content-between align-items-center">
        <h4>REEFER DETAILS</h4>
        {!reeferInstructions ? null : (
          <span className="font-weight-bold" data-cy="reefer-booking-genset">
            GENSET: {reeferInstructions.genset}
          </span>
        )}
      </div>
      <div className="card-body grid-4">
        <div>
          <StyledTextField
            fullWidth
            type="number"
            label="TEMPERATURE"
            value={container.temperature ?? ''}
            onChange={e => updateTemperature(e.target.value)}
            inputProps={{ step: 'any' }}
            InputProps={{ endAdornment: <InputAdornment position="end">°C</InputAdornment> }}
            helperText={instruction('temperature')}
            disabled={noUnit}
            data-cy="reefer-temperature-input"
          />
        </div>
        <div>
          <StyledTextField
            fullWidth
            type="string"
            label="VENTILATION"
            value={container.ventilation ?? ''}
            onChange={e => updateVentilation(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            helperText={instruction('ventilation')}
            disabled={noUnit}
            data-cy="reefer-ventilation-input"
          />
        </div>
        <div>
          <StyledTextField
            fullWidth
            type="number"
            label="HUMIDITY"
            value={container.humidity ?? ''}
            onChange={e => updateHumidity(e.target.value)}
            inputProps={{ step: 'any' }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            helperText={instruction('humidity')}
            disabled={noUnit}
            data-cy="reefer-humidity-input"
          />
        </div>
        <div>
          <StyledTextField
            fullWidth
            type="number"
            label="FUEL LEVEL"
            value={genset.fuelLevel}
            onChange={e => updateFuelLevel(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">/ 4</InputAdornment> }}
            disabled={!genset?.unitNumber?.length}
            data-cy="genset-fuel-level-input"
          />
        </div>
      </div>
    </div>
  );
};

export default ReeferInformation;
